import { birthdaySchema } from '../household/index.js';
import { parseCalendarEvent } from './schema.js';
const isoNow = () => new Date().toISOString();
function stamp(ctx) {
    return (ctx.now ?? isoNow)();
}
function runInTransaction(map, fn) {
    if (map.doc)
        map.doc.transact(fn);
    else
        fn();
}
// ---------------------------------------------------------------------------------------
// Pure record builders
// ---------------------------------------------------------------------------------------
/** A new event with all four audit fields stamped by `actorId` at one instant. */
export function createEventRecord(input, ctx) {
    const now = stamp(ctx);
    return {
        ...input,
        id: input.id ?? ctx.newId(),
        createdBy: ctx.actorId,
        createdAt: now,
        updatedBy: ctx.actorId,
        updatedAt: now,
    };
}
/** `existing` with `patch` applied; identity and creation audit can never be patched. */
export function applyEventPatch(existing, patch, ctx) {
    return {
        ...existing,
        ...patch,
        id: existing.id,
        createdBy: existing.createdBy,
        createdAt: existing.createdAt,
        updatedBy: ctx.actorId,
        updatedAt: stamp(ctx),
    };
}
/** A new birthday for the `household` doc's birthdays map, audit fields stamped. */
export function createBirthdayRecord(input, ctx) {
    const now = stamp(ctx);
    return {
        ...input,
        id: input.id ?? ctx.newId(),
        createdBy: ctx.actorId,
        createdAt: now,
        updatedBy: ctx.actorId,
        updatedAt: now,
    };
}
// ---------------------------------------------------------------------------------------
// Map mutations
// ---------------------------------------------------------------------------------------
export function insertEvent(map, input, ctx) {
    const event = createEventRecord(input, ctx);
    map.set(event.id, event);
    return event;
}
/** Patches the event in place; `null` when `id` is not a valid event in this map. */
export function updateEventIn(map, id, patch, ctx) {
    const existing = parseCalendarEvent(map.get(id));
    if (!existing)
        return null;
    const next = applyEventPatch(existing, patch, ctx);
    map.set(id, next);
    return next;
}
/** Removes the event and hands back what was there (for an undo), or `null`. */
export function deleteEventIn(map, id) {
    const existing = parseCalendarEvent(map.get(id));
    if (!existing)
        return null;
    map.delete(id);
    return existing;
}
/** Puts a deleted event back as it was, re-stamping only the update audit. */
export function restoreEventIn(map, event, ctx) {
    const restored = { ...event, updatedBy: ctx.actorId, updatedAt: stamp(ctx) };
    map.set(event.id, restored);
    return restored;
}
/**
 * Moves an event from one map to another (the "Just me" toggle: shared ⇄ personal doc).
 * Write-then-delete across two docs — they cannot share a transaction, and "in both for a
 * moment" is recoverable where "in neither" looks like a deletion. `toPersonal` drops any
 * subset `audience`: the personal doc *is* the rule, a stale audience would be a second,
 * weaker claim about the same event.
 */
export function moveEventBetween(from, to, id, toPersonal, ctx) {
    const existing = parseCalendarEvent(from.get(id));
    if (!existing)
        return null;
    const moved = {
        ...existing,
        ...(toPersonal ? { audience: undefined } : {}),
        updatedBy: ctx.actorId,
        updatedAt: stamp(ctx),
    };
    to.set(id, moved); // write first, then delete — never in neither doc
    from.delete(id);
    return moved;
}
/**
 * Removes one occurrence of a repeating event by adding it to `exdates`. Returns the
 * parent as it now stands (unchanged — and unwritten — when that occurrence was already
 * excluded), or `null` when the parent is missing.
 */
export function excludeOccurrenceIn(map, parentId, occurrenceStart, ctx) {
    const parent = parseCalendarEvent(map.get(parentId));
    if (!parent)
        return null;
    if (parent.exdates.includes(occurrenceStart))
        return parent;
    const next = {
        ...parent,
        exdates: [...parent.exdates, occurrenceStart],
        updatedBy: ctx.actorId,
        updatedAt: stamp(ctx),
    };
    map.set(parentId, next);
    return next;
}
/**
 * "Just this one": excludes `occurrenceStart` from the series and writes a detached,
 * non-repeating event carrying the edit — in one transaction, so peers never see the
 * occurrence missing or doubled. The detached event lives in the same map as its series
 * (splitting one occurrence out of a "Just me" event must not publish it).
 */
export function detachOccurrenceIn(map, parentId, occurrenceStart, patch, ctx) {
    const parent = parseCalendarEvent(map.get(parentId));
    if (!parent)
        return null;
    const now = stamp(ctx);
    const detached = {
        ...parent,
        ...patch,
        id: ctx.newId(),
        recurrence: undefined,
        exdates: [],
        recurrenceParentId: parent.id,
        recurrenceId: occurrenceStart,
        createdBy: ctx.actorId,
        createdAt: now,
        updatedBy: ctx.actorId,
        updatedAt: now,
    };
    runInTransaction(map, () => {
        map.set(parent.id, {
            ...parent,
            exdates: parent.exdates.includes(occurrenceStart)
                ? parent.exdates
                : [...parent.exdates, occurrenceStart],
            updatedBy: ctx.actorId,
            updatedAt: now,
        });
        map.set(detached.id, detached);
    });
    return detached;
}
/**
 * Adds a birthday to the `household` doc's birthdays map. Unlike the event helpers this
 * validates (throws a `ZodError` before writing): there is no web writer to stay
 * behaviour-compatible with, and a malformed birthday would be silently dropped by every
 * reader rather than fail loudly at the one place that can tell the user.
 */
export function addBirthday(map, input, ctx) {
    const birthday = birthdaySchema.parse(createBirthdayRecord(input, ctx));
    map.set(birthday.id, birthday);
    return birthday;
}
//# sourceMappingURL=mutations.js.map